import React from 'react';
import SEO from '../components/SEO';
import './SexLifeTracker.css';

const AndroidIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.523 15.341a.914.914 0 01-.913.912.912.912 0 01-.912-.912.913.913 0 01.912-.913.914.914 0 01.913.913zm-9.219 0a.914.914 0 01-.913.912.912.912 0 01-.912-.912.913.913 0 01.912-.913.914.914 0 01.913.913zM17.9 10l1.448-2.508a.302.302 0 00-.11-.412.302.302 0 00-.412.11l-1.468 2.542A8.965 8.965 0 0012 8.545c-1.97 0-3.79.635-5.258 1.687L5.274 7.19a.302.302 0 00-.412-.11.302.302 0 00-.11.412L6.2 10c-2.133 1.454-3.548 3.795-3.548 6.453 0 .303.017.602.048.898h18.6c.031-.296.048-.595.048-.898C21.348 13.795 19.933 11.454 17.9 10z"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const features = [
  {
    icon: '📝',
    title: 'Detailed Session Logging',
    desc: 'Log shared moments with mood, duration, notes, and satisfaction ratings. Simple multi-step form captures every detail.',
  },
  {
    icon: '📊',
    title: 'Trends & Insights',
    desc: 'View trends and insights over time. Beautiful charts and analytics to reflect on relationship growth.',
  },
  {
    icon: '👫',
    title: 'Partner Profiles',
    desc: 'Organize partner profiles and favorite locations. Track preferences and patterns to better understand your connection.',
  },
  {
    icon: '🔒',
    title: 'Passcode Protection',
    desc: 'Protect all data with secure passcode access. Privacy-first design — your data stays only on your device.',
  },
  {
    icon: '🎮',
    title: 'Couple Activities',
    desc: 'Enjoy fun couple activities like trivia and decision games. Receive thoughtful suggestions to improve connection.',
  },
  {
    icon: '💎',
    title: 'Premium Features',
    desc: 'Upgrade to Premium for full language access, enhanced game features, cleaner flow, and better polish.',
  },
];

const screenshots = [
  {
    src: '/images/sex-life-tracker/my-sexlife-tracker-screenshot-session-log.jpeg',
    alt: 'My SexLife Tracker – session logging screen',
  },
  {
    src: '/images/sex-life-tracker/my-sexlife-tracker-screenshot-trends-analytics.jpeg',
    alt: 'My SexLife Tracker – trends and analytics screen',
  },
  {
    src: '/images/sex-life-tracker/my-sexlife-tracker-screenshot-partner-profiles.jpeg',
    alt: 'My SexLife Tracker – partner profiles screen',
  },
  {
    src: '/images/sex-life-tracker/my-sexlife-tracker-screenshot-couple-games.jpeg',
    alt: 'My SexLife Tracker – couple games screen',
  },
  {
    src: '/images/sex-life-tracker/my-sexlife-tracker-screenshot-premium-features.jpeg',
    alt: 'My SexLife Tracker – premium features screen',
  },
];

const SexLifeTracker = () => (
  <div className="product-page">
    <SEO
      title="My SexLife Tracker — Private Relationship Wellness Journal | Appshow"
      description="A private relationship wellness journal designed to help couples better understand their connection, preferences, and shared experiences. Log moments, track patterns, and grow closer."
      keywords="relationship tracker, couple app, intimacy tracker, relationship journal, couple wellness, private tracker"
      url="/sexlife-tracker"
    />

    {/* Hero */}
    <section className="pp-hero">
      <div className="container pp-hero-inner">
        <div className="pp-hero-content">
        <div className="pp-hero-meta">
          <span className="badge-live">Live</span>
          <span className="pp-category">Health &amp; Fitness</span>
        </div>
        <h1 className="pp-title">My SexLife Tracker</h1>
        <p className="pp-subtitle">Sex tracker &amp; games for couple</p>
        <p className="pp-description">
          A private relationship wellness journal designed to help couples better understand their connection,
          preferences, and shared experiences. The app focuses on reflection, communication, and personal
          insights — helping couples grow closer in a respectful, secure, and meaningful way.
        </p>
        <div className="pp-store-buttons">
          <a
            href="https://play.google.com/store/apps/details?id=com.datos.sexlifetracker"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-store-large"
          >
            <AndroidIcon />
            <span>
              <span className="btn-store-sub">Get it on</span>
              <span className="btn-store-main">Google Play</span>
            </span>
          </a>
          <a
            href="https://apps.apple.com/us/app/my-sexlife-tracker/id6753984259"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-store-large"
          >
            <AppleIcon />
            <span>
              <span className="btn-store-sub">Download on the</span>
              <span className="btn-store-main">App Store</span>
            </span>
          </a>
        </div>
        <p className="pp-note">Free · In-App Purchases · iOS 15.1+ · Android</p>
        </div>
        <div className="pp-hero-preview">
          <img
            src="/images/sex-life-tracker/my-sexlife-tracker-app-preview.png"
            alt="My SexLife Tracker app preview – couple relationship wellness journal"
            className="pp-preview-img"
          />
        </div>
      </div>
    </section>

    {/* Screenshots */}
    <section className="pp-screenshots">
      <div className="container">
        <h2 className="pp-section-title">Screenshots</h2>
        <div className="screenshots-row">
          {screenshots.map((s, i) => (
            <div className="phone-frame" key={i}>
              <img src={s.src} alt={s.alt} loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Features */}
    <section className="pp-features">
      <div className="container">
        <h2 className="pp-section-title">Why couples love it</h2>
        <div className="features-grid">
          {features.map((f, i) => (
            <div className="feature-card" key={i}>
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="pp-cta">
      <div className="container">
        <h2>Ready to strengthen your connection?</h2>
        <p>Download free today. Upgrade to Premium anytime.</p>
        <div className="pp-store-buttons">
          <a href="https://play.google.com/store/apps/details?id=com.datos.sexlifetracker" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AndroidIcon /><span><span className="btn-store-sub">Get it on</span><span className="btn-store-main">Google Play</span></span>
          </a>
          <a href="https://apps.apple.com/us/app/my-sexlife-tracker/id6753984259" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AppleIcon /><span><span className="btn-store-sub">Download on the</span><span className="btn-store-main">App Store</span></span>
          </a>
        </div>
      </div>
    </section>
  </div>
);

export default SexLifeTracker;
